import { useEffect } from 'react';
import { sessionStorageWrapper } from '@/utils/browser-state';

const cssVisionFilterVarName = '--page-color-vision-deficiency-filter';
export const queryStringKey = 'filter';
export const localStorageKey = 'PbsKidsPuma.vision.filter';

export enum VisionFilters {
  'noFilter' = '',
  'protanopia' = 'protanopia',
  'deuteranopia' = 'deuteranopia',
  'tritanopia' = 'tritanopia',
  'achromatopsia' = 'achromatopsia',
  'blurred' = 'blurred-vision',
  'lowContrast' = 'low-contrast',
}

const VisionFilterValues: string[] = Object.values(VisionFilters);

export function setVisionFilter(filter: VisionFilters) {
  if (filter === VisionFilters.noFilter) {
    document.body.style.setProperty(cssVisionFilterVarName, '');
  } else {
    document.body.style.setProperty(cssVisionFilterVarName, `url(#${filter})`);
  }

  sessionStorageWrapper.set(localStorageKey, filter);
}

export default function VisionDeficiencyEmulator({ filter }: { filter?: string }) {
  useEffect(() => {
    let selectedFilter;

    // Add CSS filter to document body.
    document.body.style.filter = `var(${cssVisionFilterVarName})`;

    if (filter) {
      // 1. Validate the filter setting from the query string.
      selectedFilter = filter.toLowerCase();

      if (!VisionFilterValues.includes(selectedFilter)) {
        // If the qsp filter is NOT a valid vision filter,
        // then reset to no filter and correct the url.
        selectedFilter = VisionFilters.noFilter;
        window.history.replaceState(null, '', `?${queryStringKey}=`);
      }
    } else {
      // 2. Attempt to retrieve the filter from session storage.
      selectedFilter = sessionStorageWrapper.get(localStorageKey) || VisionFilters.noFilter;
    }

    // 3. Apply filter.
    setVisionFilter(selectedFilter as VisionFilters);
  }, [ filter ]);

  return <svg width='0' height='0' style={{ height: 0, width: 0 }}>
    {/* Matrices obtained from https://www.inf.ufrgs.br/%7Eoliveira/pubs_files/CVD_Simulation/CVD_Simulation.html */}
    <filter id='protanopia'>
      <feColorMatrix
        type='matrix'
        values='0.152286 1.052583 -0.204868 0 0
                0.114503 0.786281 0.099216 0 0
                -0.003882 -0.048116 1.051998 0 0
                0      0      0    1  0' />
    </filter>

    <filter id='deuteranopia'>
      <feColorMatrix
        type='matrix'
        values='0.367322 0.860646 -0.227968 0 0
                0.280085 0.672501 0.047413 0  0
                -0.011820 0.042940 0.968881 0 0
                0      0      0    1  0' />
    </filter>

    <filter id='tritanopia'>
      <feColorMatrix
        type='matrix'
        values='1.255528 -0.076749 -0.178779 0 0
                -0.078411 0.930809 0.147602 0 0
                0.004733 0.691367 0.303900 0 0
                0      0      0    1  0' />
    </filter>

    <filter id='achromatopsia'>
      <feColorMatrix
        type='matrix'
        values='0.213  0.715  0.072  0  0
                0.213  0.715  0.072  0  0
                0.213  0.715  0.072  0  0
                0      0      0    1  0' />
    </filter>

    {/* Blurred and Contrast filters obtained from https://source.chromium.org/chromium/chromium/src/+/main:third_party/blink/renderer/core/css/vision_deficiency.cc */}
    <filter id='blurred-vision'>
      <feGaussianBlur in='SourceGraphic' stdDeviation='2'/>
    </filter>

    <filter id='low-contrast'>
      <feComponentTransfer>
        <feFuncR type='gamma' offset='0.5'/>
        <feFuncG type='gamma' offset='0.5'/>
        <feFuncB type='gamma' offset='0.5'/>
      </feComponentTransfer>
    </filter>
  </svg>;
}
